import React, { useEffect, useState } from "react";
import { Form, Button, message, Modal, Tabs, Spin, Alert } from "antd";
import styles from "../management.module.scss";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/hook";
import { custInfRecordProps } from "../index";
import { useUser, useCreateUser, useUpdateUser, useDeleteUser, useDeviceGroup } from "../queries";
import CreateUserModal from "./UserModalF_Create";
import EditUserModal from "./UserModalF_Edit";

const { TabPane } = Tabs;

interface UserModalProps {
  record: custInfRecordProps;
  setRecord: (arg: custInfRecordProps) => void;
  CreateUservisible: boolean;
  setCreateUservisible: (arg: boolean) => void;
}

const UserModaC = React.memo(({ setRecord, record, CreateUservisible, setCreateUservisible }: UserModalProps) => {
  const [CreateUserform] = Form.useForm();
  const [EditUserform] = Form.useForm();
  const [currentPage, setCurrentPage] = useState("1");
  const { t } = useTranslation();
  const { level } = useAppSelector((state) => state.global);
  const [editingKey, setEditingKey] = useState("");
  const { data: users } = useUser(record?.cid, level, CreateUservisible);
  EditUserform.setFieldsValue({
    EditUser: users,
  });
  const { mutateAsync: createUserMutation, isLoading: createUserLoading } = useCreateUser(record?.cid);
  const { mutateAsync: updateUserMutation, isLoading: updateUserLoading } = useUpdateUser(record?.cid);
  const { mutateAsync: deleteUserMutation, isLoading: deleteUserLoading } = useDeleteUser(record?.cid);
  const { data: deviceGroups, isFetching: deviceGroupsFetching } = useDeviceGroup(record?.cid, CreateUservisible);
  const editFetching = deviceGroupsFetching || updateUserLoading || deleteUserLoading;
  useEffect(() => {
    CreateUserform.setFieldsValue({
      cid: record.cid,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CreateUservisible]);

  const ChangeTabs = (page: string) => {
    setCurrentPage(`${page}`);
  };

  const deleteUser = (Deleterecord: any) => {
    deleteUserMutation({ cid: record.cid, name: Deleterecord.name })
      .then(() => {
        message.success(t("ISMS.Success"));
      })
      .catch(() => {
        message.error(t("ISMS.Fail"));
      })
      .finally(() => {
        CreateUserform.resetFields();
      });
  };

  const createUsers = (values: any) => {
    if (!values.users) {
      return;
    }

    let usersList = new Set();
    values.users.forEach((user: any) => usersList.add(user.name));

    if (Array.from(usersList).length !== values.users.length) {
      message.error(t("ISMS.Duplicated"));
      return;
    }

    createUserMutation({ cid: record.cid, values })
      .then(() => {
        message.success(t("ISMS.Success"));
        CreateUserform.setFieldsValue({ users: [] });
        setCurrentPage("2");
      })
      .catch(() => {
        message.error(t("ISMS.Fail"));
      });
  };

  const EditUseronFinish = (values: any) => {
    values.EditUser.forEach((item: any) => {
      if (!item.password) {
        delete item.password;
      }
    });

    updateUserMutation({ cid: record.cid, values })
      .then(() => {
        message.success(t("ISMS.Success"));
        setCurrentPage("2");
        setEditingKey("");
      })
      .catch(() => {
        message.error(t("ISMS.Fail"));
      })
      .finally(() => {
        EditUserform.resetFields();
      });
  };

  const createProps = { deviceGroups, CreateUserform, createUsers };

  const editProps = {
    EditUserform,
    deviceGroupsFetching,
    users,
    deviceGroups,
    editingKey,
    setEditingKey,
    deleteUser,
    EditUseronFinish,
  };

  return (
    <Modal
      title={t("ISMS.User")}
      visible={CreateUservisible}
      centered
      onCancel={() => {
        setCreateUservisible(false);
        setRecord({ cid: undefined });
        CreateUserform.setFieldsValue({ users: [] });
        setCurrentPage("1");
        setEditingKey("");
      }}
      className={styles.modal}
      destroyOnClose={true}
      footer={[
        currentPage === "1" && (
          <Button
            key="Create"
            type="primary"
            loading={createUserLoading}
            onClick={() => {
              CreateUserform.submit();
            }}
          >
            {t("ISMS.Submit")}
          </Button>
        ),
        currentPage === "2" && (
          <Button
            key="Edit"
            type="primary"
            loading={editFetching}
            onClick={() => {
              if (editingKey !== "") {
                EditUserform.submit();
              } else {
                setCreateUservisible(false);
                setCurrentPage("1");
              }
            }}
          >
            {editingKey !== "" ? t("ISMS.Submit") : t("ISMS.Confirm")}
          </Button>
        ),
      ]}
    >
      {deviceGroupsFetching ? (
        <Spin tip="Loading...">
          <Alert
            message="Getting Data"
            description="We are now getting data from server, please wait for a few seconds"
          />
        </Spin>
      ) : (
        <Tabs defaultActiveKey="1" onChange={ChangeTabs} activeKey={currentPage}>
          <TabPane
            // tab="Create User"
            tab={t("ISMS.CreateUser")}
            key="1"
          >
            <CreateUserModal {...createProps} />
          </TabPane>
          <TabPane
            // tab="Edit User"
            tab={t("ISMS.EditUser")}
            key="2"
          >
            <EditUserModal {...editProps} />
          </TabPane>
        </Tabs>
      )}
    </Modal>
  );
});

export default UserModaC;
